import { Component, inject } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { LetDirective } from '@ngrx/component';
import ObjectId from 'bson-objectid';
import { BehaviorSubject, filter, switchMap, tap } from 'rxjs';

import { Company } from '@ninety/platform/models/company/company';
import { HelpfulService } from '@ninety/platform/services';
import { CompanyService } from '@ninety/platform/state';
import { TerraFormFieldModule, TerraTextInputModule, TerraIconModule, TerraErrorModule } from '@ninety/terra';
import {
  ButtonComponent,
  CardComponent,
  CardHeaderComponent,
  ConfirmDialogData,
  WarningConfirmDialogComponent,
} from '@ninety/ui/core';

@Component({
  selector: 'ninety-hard-delete-company',
  templateUrl: './hard-delete-company.component.html',
  styleUrls: ['../_shared/tab-styles.scss', './hard-delete-company.component.scss'],
  standalone: true,
  imports: [
    LetDirective,
    CardComponent,
    CardHeaderComponent,
    TerraFormFieldModule,
    TerraTextInputModule,
    FormsModule,
    ReactiveFormsModule,
    TerraIconModule,
    TerraErrorModule,
    ButtonComponent,
  ],
})
export class HardDeleteCompanyComponent {
  private readonly helpfulService = inject(HelpfulService);
  private readonly dialog = inject(MatDialog);
  private readonly companyService = inject(CompanyService);

  private readonly _companyDeletionResult$ = new BehaviorSubject<string>('');
  protected readonly companyDeletionResult$ = this._companyDeletionResult$.asObservable();

  companyIdToDeleteSearch = new FormControl('', {
    validators: [
      (c: AbstractControl): ValidationErrors =>
        !c.value || ObjectId.isValid(c.value) ? null : { isObjectId: { value: c.value } },
    ],
  });

  companyToDelete: Company;

  findCompanyById() {
    const companyId = this.companyIdToDeleteSearch.value.trim();

    if (!ObjectId.isValid(companyId)) {
      this.companyIdToDeleteSearch.markAsTouched();
      this.companyIdToDeleteSearch.updateValueAndValidity();
      return;
    }

    this.companyService.getCompanyById(companyId).subscribe({
      next: company => (this.companyToDelete = company),
    });
  }

  openDeleteCompanyDialog(): void {
    this.dialog
      .open<WarningConfirmDialogComponent, ConfirmDialogData>(WarningConfirmDialogComponent, {
        data: {
          title: `Hard Delete Company ${this.companyToDelete.name}?`,
          message: '<strong>Caution:</strong> This action cannot be undone.',
          confirmButtonText: 'I understand... Delete Anyway',
        },
      })
      .afterClosed()
      .pipe(
        filter(shouldDelete => shouldDelete),
        switchMap(() => this.helpfulService.hardDeleteCompany(this.companyToDelete._id)),
        tap(deleteResult => this._companyDeletionResult$.next(deleteResult))
      )
      .subscribe();
  }
}
